import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { v4 } from 'uuid';

import { AlfredFooter } from '~/components/alfred-footer/AlfredFooter';
import { RenderFeatureFlag } from '~/components/render-feature-flag/RenderFeatureFlag';
import { fetchContentQuestions } from '~/services/content-questions/content-questions-service';
import { useAlfredFooterStore } from '~/stores/use-alfred-footer/useAlfredFooter';
import { useChatInputStore } from '~/stores/use-chat-input/useChatInput';
import { FeatureFlag, useFlags } from '~/stores/use-flags/useFlags';
import { Watchlist } from '~/stores/use-watchlist/watchlist-types';

import { ActiveFilterOptions } from '../filters/Filters';
import * as S from './WithFooterWrapper.styles';

const QUERY_KEY_CONTENT_QUESTIONS = 'QUERY_KEY_CONTENT_QUESTIONS';

export interface WithFooterWrapperProps {
  children?: React.ReactNode;
  flag?: FeatureFlag;
  fallback?: React.ReactNode;
  testId?: string;
  isWrappedComponentOpened?: boolean;
  className?: string;
  emptyState?: boolean;
  questionContext?: ActiveFilterOptions[] | string | string[] | Watchlist;
  shouldHide?: boolean;
  enableAutoRun?: boolean;
  showReloadButton?: boolean;
}

export const WithFooterWrapper = ({
  children,
  flag = 'alfred_everywhere_feature',
  fallback = null,
  testId,
  isWrappedComponentOpened = true,
  className,
  emptyState = false,
  questionContext,
  shouldHide = false,
  enableAutoRun = false,
  showReloadButton = false,
}: WithFooterWrapperProps) => {
  const contentRef = useRef<HTMLElement | null>(null);
  const [questions, setQuestions] = useState<string[]>([]);
  const [queryCounter, setQueryCounter] = useState(0);
  const {
    hasUserInteracted,
    askedQuestions,
    setHasUserInteracted,
    addNewAskedQuestion,
  } = useChatInputStore(state => ({
    hasUserInteracted: state.hasUserInteracted,
    askedQuestions: state.askedQuestions,
    setHasUserInteracted: state.setHasUserInteracted,
    addNewAskedQuestion: state.addNewAskedQuestion,
  }));
  const isFlagEnabled = useFlags(state => state.getFeatureFlagState(flag));

  const idRef = useRef<string>('');

  if (!idRef.current) {
    idRef.current = v4();
  }

  const { activeId, push, remove } = useAlfredFooterStore(state => ({
    activeId: state.activeId,
    push: state.push,
    remove: state.remove,
  }));

  useEffect(() => {
    if (isWrappedComponentOpened) {
      push(idRef.current);
      return () => remove(idRef.current);
    }

    return undefined;
  }, [isWrappedComponentOpened, push, remove]);

  const resetQuestions = () => {
    setQuestions([]);
  };

  const isFooterActive = activeId === idRef.current;

  useEffect(() => {
    resetQuestions();
  }, [questionContext]);

  const { data, isLoading } = useQuery({
    queryKey: [
      QUERY_KEY_CONTENT_QUESTIONS,
      queryCounter,
      askedQuestions.length,
    ],
    queryFn: ({ signal }) => fetchContentQuestions(signal),
    enabled:
      !emptyState &&
      !hasUserInteracted &&
      isWrappedComponentOpened &&
      isFooterActive &&
      isFlagEnabled &&
      enableAutoRun,
  });

  useEffect(() => {
    if (hasUserInteracted) {
      setQuestions([]);
    } else {
      setQuestions(data?.questions ?? []);
    }
  }, [data]);

  const onReloadQuestion = () => {
    setHasUserInteracted(false);
    resetQuestions();
    setQueryCounter(prev => prev + 1);
  };

  const question = questions[0];

  const onSubmitFromChat = () => {
    resetQuestions();
    onReloadQuestion();
    addNewAskedQuestion(question);
  };

  return (
    <S.Root data-testid={testId}>
      <S.Content ref={contentRef} className={className}>
        {children}
      </S.Content>
      {isWrappedComponentOpened && (
        <RenderFeatureFlag requiredFlag={flag} fallback={fallback}>
          <AlfredFooter
            suggestedQuestion={
              isFooterActive && enableAutoRun ? question : undefined
            }
            isActive={isFooterActive}
            isLoading={isLoading}
            emptyState={emptyState}
            shouldHide={shouldHide}
            onReload={onReloadQuestion}
            submitCallback={onSubmitFromChat}
            showReloadButton={showReloadButton}
          />
        </RenderFeatureFlag>
      )}
    </S.Root>
  );
};
